import { useState, useRef, useEffect } from "react";

/**
 * SearchableDropdown component
 * A custom dropdown with search functionality that works consistently across all devices
 * 
 * @param {Array} options - Array of objects with { value, label } structure
 * @param {string} value - Current selected value
 * @param {Function} onChange - Callback when selection changes
 * @param {string} placeholder - Placeholder text
 * @param {string} label - Label for the dropdown
 * @param {Function} onFocus - Optional onFocus callback
 * @param {string} className - Optional additional CSS classes
 */
function SearchableDropdown({
    options = [],
    value,
    onChange,
    placeholder = "Select...",
    label,
    onFocus,
    className = ""
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm("");
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Filter options based on search term
    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Get the label of the currently selected option
    const selectedLabel = options.find(opt => opt.value === value)?.label || "";

    const handleSelect = (selectedValue) => {
        onChange(selectedValue);
        setIsOpen(false);
        setSearchTerm("");
    };

    const handleOpen = () => {
        setIsOpen(true);
        if (onFocus) onFocus();
        // Focus the search input when opening
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 100);
    };

    return (
        <div ref={dropdownRef} className="relative w-full">
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                </label>
            )}

            {/* Trigger Button */}
            <button
                type="button"
                onClick={handleOpen}
                className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-left focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white flex items-center justify-between ${className}`}
            >
                <span className={value ? "text-gray-900" : "text-gray-400"}>
                    {selectedLabel || placeholder}
                </span>
                <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-2 border-b border-gray-200">
                        <input
                            ref={inputRef}
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                        />
                    </div>

                    {/* Options List */}
                    <div className="overflow-y-auto max-h-48">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleSelect(option.value)}
                                    className={`w-full text-left px-3 py-2 hover:bg-green-50 transition-colors ${option.value === value ? 'bg-green-100 text-green-900 font-medium' : 'text-gray-900'
                                        }`}
                                >
                                    {option.label}
                                </button>
                            ))
                        ) : (
                            <div className="px-3 py-4 text-center text-gray-500 text-sm">
                                No results found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default SearchableDropdown;
